import { Axis, type Island, Vertex3 } from '@rta/rta-blueprint-library';

import { DimensionLine } from '@r-components';
import type { ComponentController } from '@r-core/controller';
import { getAdjoiningCountertopSeams } from '../helpers/get-adjoining-countertop-seams';
import { getCleanEdgeLabels } from '../helpers/generate-clean-edge-annotations';
import { getInlineIslandChain, layoutInlineRun } from '../../helpers/get-inline-island-chain';
import { getJointCountertopLengths } from '../helpers/get-joint-countertop-lengths';
import { RenderAdjoiningSeamMarker } from './render-adjoining-seam-marker';
import { RenderCleanEdgeLabel } from './render-clean-edge-label';
import { RenderCountertop } from '../../render/render-countertop';

export type RenderJointCountertopRowProps = {
	controller: ComponentController;
	islandIds: Array<string>;
};

/**
 * A joint countertop sheet, laid out exactly like the (working) inline countertop view lays out
 * the same run — same left-to-right chain order and cumulative-width/padding math as
 * show-inline-islands.tsx (getInlineIslandChain/getInlineIslandPadding), with the chain walk
 * restricted to this joint group's own members (another group's run can meet this one at a right
 * angle with no Corner island between them, so an unrestricted walk could wander into it and never
 * find its way back) and positioned through the SAME layoutInlineRun the inline view calls. Each island's
 * top still renders in its own local (unrotated) frame via RenderCountertop, with its own
 * geometry-based CLEAN EDGE and SEAM markers, so a same-height abutment shows its seam on
 * whichever grouped island it lands on. The joint slab has one uniform depth, so RenderCountertop's
 * depth (side-panel) dimension is shown exactly once — via hideDepthDimension, on whichever member
 * ends up farthest right in final drawn position — and suppressed on every other
 * member. getCleanEdgeLabels/getAdjoiningCountertopSeams both drop their own marker at the
 * internal joining edge between two members of the same joint group. #551: the row owns the whole
 * length story -- one combined total plus one dimension per stretch between seams (each end stretch
 * running from the outermost seam to the far end of the slab), and no member draws its own
 * back-edge length or breakpoint segments (hideLengthDimension).
 */
export const RenderJointCountertopRow = (props: RenderJointCountertopRowProps): React.ReactElement => {
	const { controller, islandIds } = props;
	const { kitchen } = controller;

	const memberIds = new Set(islandIds);
	const groupIslands = islandIds
		.map((id) => kitchen.design.islands.findById(id))
		.filter((island): island is Island => Boolean(island));
	if (groupIslands.length === 0) return <></>;

	// Same left-to-right connection-chain order the inline view uses, restricted to this group's members
	// so the walk can't wander off through a perpendicular neighbor (e.g. another joint group's run
	// meeting this one at a right angle) and lose its way back — see getInlineIslandChain's allowedIds.
	const orderedGroup = getInlineIslandChain(groupIslands[0], memberIds);

	// Literally the same layout call the inline view makes, so the two sheets cannot disagree about which
	// way a run reads (#646/#656). The sheet is read from the slab's front, so the view angle is the
	// group's own facing; order and spacing then come from the islands' real coordinates.
	const { positionOf: getAlongRowPosition } = layoutInlineRun({
		chain: orderedGroup,
		widthOf: (island: Island) => island.islandSize.width,
		viewAngle: orderedGroup[0].axis.y
	});

	// Picked by final drawn position, not array index — the run's order comes from coordinates, so which
	// member ends up rightmost depends on the viewing direction.
	const farthestRightIslandId = orderedGroup.reduce((farthest, island) => (
		getAlongRowPosition(island) > getAlongRowPosition(farthest) ? island : farthest
	), orderedGroup[0]).id;

	// #304/#551: the row owns the whole length story -- one combined total across the joint slab, split
	// at each seam into row-wide stretches. No member draws its own back-edge length (hideLengthDimension).
	const rowBackEdge = getJointCountertopLengths({ controller, orderedGroup, getAlongRowPosition });
	// Same isLevel rule render-countertop uses: a 180°-facing back panel needs the label group's own
	// counter-rotation, or DimensionLine's text flip is uncancelled and the number reads upside down.
	const isLevel = rowBackEdge?.panelAxis === 0;

	return <>
		{rowBackEdge && <DimensionLine
			length={rowBackEdge.length}
			axis={new Axis({ dataIn: { x: 0, y: rowBackEdge.panelAxis, z: 0 } })}
			includeLabelBox={true}
			topView={true}
			level={isLevel}
			canMove={true}
			dragConstraint="1d"
			indicatorStart="flat" indicatorEnd="flat"
			padding={(rowBackEdge.overhang / 2) + 9}
			bBox={{
				origin: new Vertex3({ dataIn: { x: rowBackEdge.centerX, y: rowBackEdge.center.y, z: rowBackEdge.center.z } }),
				size: { width: rowBackEdge.length, height: rowBackEdge.length, depth: rowBackEdge.length }
			}}
		/>}
		{rowBackEdge?.segments.map((segment) => <DimensionLine
			key={`seam-segment-${segment.start}`}
			length={segment.end - segment.start}
			axis={new Axis({ dataIn: { x: 0, y: rowBackEdge.panelAxis, z: 0 } })}
			includeLabelBox={true}
			topView={true}
			level={isLevel}
			canMove={true}
			dragConstraint="1d"
			indicatorStart="flat" indicatorEnd="flat"
			padding={(rowBackEdge.overhang / 2) + 3.75}
			bBox={{
				origin: new Vertex3({ dataIn: { x: (segment.start + segment.end) / 2, y: rowBackEdge.center.y, z: rowBackEdge.center.z } }),
				size: { width: segment.end - segment.start, height: rowBackEdge.length, depth: rowBackEdge.length }
			}}
		/>)}
		{orderedGroup.map((island) => {
		const alongRowPosition = getAlongRowPosition(island);
		const seams = getAdjoiningCountertopSeams({ island, kitchen });
		const cleanEdges = getCleanEdgeLabels({ island, kitchen });

		return <g key={island.id} transform={`translate(${alongRowPosition}, 0)`}>
			<RenderCountertop islandId={island.id} hideDepthDimension={island.id !== farthestRightIslandId} hideLengthDimension={true} />
			{seams.map((marker) => <RenderAdjoiningSeamMarker key={marker.id} marker={marker} fontSize={2} />)}
			{cleanEdges.map((label) => <RenderCleanEdgeLabel key={label.id} label={label} fontSize={2.2} />)}
		</g>;
	})}</>;
};
